import { throttle } from "lodash-es"
import React from "react"

interface UseScrollHandlerProps {
  onCleanUp?: () => void
  onResize?: () => void
  onScroll: () => void
  runForProps?: React.DependencyList
}

const useScrollHandler = ({
  onCleanUp,
  onResize,
  onScroll,
  runForProps = [],
}: UseScrollHandlerProps) => {
  // Stable scroll handler with RAF
  const onScrollHandler = React.useCallback(() => {
    if (typeof window !== "undefined") {
      window.requestAnimationFrame(onScroll)
    }
  }, [onScroll])

  // Stable throttled resize handler
  const onResizeThrottled = React.useMemo(
    () => (onResize ? throttle(onResize, 100) : null),
    [onResize]
  )

  React.useEffect(() => {
    if (typeof window === "undefined") return

    // Scroll on window, not document
    window.addEventListener("scroll", onScrollHandler, { passive: true })

    if (onResizeThrottled) {
      window.addEventListener("resize", onResizeThrottled)
    }

    return () => {
      window.removeEventListener("scroll", onScrollHandler)

      if (onResizeThrottled) {
        window.removeEventListener("resize", onResizeThrottled)
        onResizeThrottled.cancel()
      }

      onCleanUp?.()
    }
  }, [...runForProps, onScrollHandler, onResizeThrottled])
}

export default useScrollHandler
